import React from 'react';
import { Check, X, ArrowRight } from 'lucide-react';

/**
 * MeshVsRepeaters component
 * Explains the differences between mesh systems and wireless repeaters
 */
const MeshVsRepeaters = () => {
  // Comparison data
  const comparisonData = [
    {
      aspect: 'Network Type',
      mesh: 'Single unified network with one SSID',
      repeater: 'Multiple separate networks with different SSIDs',
      meshIcon: <Check className="h-5 w-5 text-green-400" />,
      repeaterIcon: <X className="h-5 w-5 text-red-400" />
    },
    {
      aspect: 'Handoff',
      mesh: 'Seamless device handoff between nodes',
      repeater: 'Manual reconnection required when moving between areas',
      meshIcon: <Check className="h-5 w-5 text-green-400" />,
      repeaterIcon: <X className="h-5 w-5 text-red-400" />
    },
    {
      aspect: 'Speed',
      mesh: 'Maintains high speeds throughout coverage area',
      repeater: 'Speed degrades with each hop (up to 50% loss)',
      meshIcon: <Check className="h-5 w-5 text-green-400" />,
      repeaterIcon: <X className="h-5 w-5 text-red-400" />
    },
    {
      aspect: 'Management',
      mesh: 'Central management through one app/interface',
      repeater: 'Each device must be configured separately',
      meshIcon: <Check className="h-5 w-5 text-green-400" />,
      repeaterIcon: <X className="h-5 w-5 text-red-400" />
    },
    {
      aspect: 'Intelligent Routing',
      mesh: 'Automatically routes data via optimal path',
      repeater: 'Fixed routing path regardless of congestion',
      meshIcon: <Check className="h-5 w-5 text-green-400" />,
      repeaterIcon: <X className="h-5 w-5 text-red-400" />
    },
    {
      aspect: 'Coverage',
      mesh: 'Comprehensive coverage with no dead zones',
      repeater: 'Inconsistent coverage with potential dead zones',
      meshIcon: <Check className="h-5 w-5 text-green-400" />,
      repeaterIcon: <X className="h-5 w-5 text-red-400" />
    }
  ];

  return (
    <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-colors mt-8">
      <h3 className="text-purple-400 font-semibold text-xl mb-4">Mesh vs. Wireless Repeaters</h3>
      
      <div className="mb-6">
        <p className="text-gray-300">
          While both mesh systems and wireless repeaters aim to extend your WiFi coverage, they work in fundamentally different ways, 
          resulting in very different user experiences. Here's why mesh technology is superior:
        </p>
      </div>
      
      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="py-3 px-4 text-left text-gray-400 font-medium">Aspect</th>
              <th className="py-3 px-4 text-left text-purple-400 font-medium">Mesh System</th>
              <th className="py-3 px-4 text-left text-red-400 font-medium">Wireless Repeater</th>
            </tr>
          </thead>
          <tbody>
            {comparisonData.map((item, index) => (
              <tr key={index} className={index < comparisonData.length - 1 ? "border-b border-gray-700/50" : ""}>
                <td className="py-4 px-4 text-white font-medium">{item.aspect}</td>
                <td className="py-4 px-4">
                  <div className="flex items-start">
                    <div className="mt-1 mr-3">{item.meshIcon}</div>
                    <span className="text-gray-300">{item.mesh}</span>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-start">
                    <div className="mt-1 mr-3">{item.repeaterIcon}</div>
                    <span className="text-gray-300">{item.repeater}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Why Repeaters Are Bad */}
      <div className="mt-8 bg-red-900/20 p-5 rounded-lg border border-red-800/30">
        <h4 className="text-red-400 font-semibold text-lg mb-3">Why Wireless Repeaters Are Problematic</h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start">
            <X className="h-5 w-5 text-red-400 mr-3 mt-1 flex-shrink-0" />
            <span>
              <strong className="text-red-300">Speed Degradation:</strong> Repeaters cut your bandwidth by up to 50% with each "hop" because they use the same radio to receive and retransmit data.
            </span>
          </li>
          <li className="flex items-start">
            <X className="h-5 w-5 text-red-400 mr-3 mt-1 flex-shrink-0" />
            <span>
              <strong className="text-red-300">Connection Instability:</strong> Devices often get "stuck" on a distant repeater even when closer to the main router, causing poor performance.
            </span>
          </li>
          <li className="flex items-start">
            <X className="h-5 w-5 text-red-400 mr-3 mt-1 flex-shrink-0" />
            <span>
              <strong className="text-red-300">Multiple Networks:</strong> Managing different network names (SSIDs) creates confusion and requires manual switching between networks.
            </span>
          </li>
          <li className="flex items-start">
            <X className="h-5 w-5 text-red-400 mr-3 mt-1 flex-shrink-0" />
            <span>
              <strong className="text-red-300">Interference:</strong> Repeaters often increase network congestion and interference, degrading overall network performance.
            </span>
          </li>
        </ul>
      </div>
      
      {/* Mesh Advantage */}
      <div className="mt-6 bg-green-900/20 p-5 rounded-lg border border-green-800/30">
        <h4 className="text-green-400 font-semibold text-lg mb-3">The Mesh Advantage</h4>
        <p className="text-gray-300 mb-4">
          Our free-to-use router is part of a mesh system that solves all these problems. With dedicated backhaul channels, 
          intelligent routing, and seamless handoff between nodes, you'll experience consistent, high-speed connectivity throughout your home.
        </p>
        <div className="flex justify-center mt-6">
          <a 
            href="/wireless-wonder" 
            className="inline-flex items-center px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full transition-colors"
          >
            Forecast Your Home Coverage
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default MeshVsRepeaters;